import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
export function TransformationSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const slides = [{
    image: 'https://cdn.magicpatterns.com/uploads/oa1dhFbZ1h1SgGdNkY6BQS/1_Stressful_office_at_twilight.png',
    title: 'El Problema',
    subtitle: 'Caos constante'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/3j7XPUhNPJPrZGs9FEwh55/2_Focused_productivity_at_sunset.png',
    title: 'La Solución',
    subtitle: 'Sistema visual'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/dM5raEbh2Y8YMRKDUNP1Xo/3_From_chaos_to_productivity.png',
    title: 'Transformación',
    subtitle: 'Del caos al control'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/bnDqpgeF51bQz6n4NYgRY2/4_Focused_and_focused_in_the_city.png',
    title: 'El Proceso',
    subtitle: 'Enfoque total'
  }, {
    image: 'https://cdn.magicpatterns.com/uploads/c4jkHshRydxQEi96DW2R19/5_Focused_morning_work_session.png',
    title: 'El Resultado',
    subtitle: 'Productividad real'
  }];
  // Auto-advance slides every 3.5 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);
  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };
  const nextSlide = () => {
    goToSlide((currentSlide + 1) % slides.length);
  };
  const prevSlide = () => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  };
  return <section className="py-10 bg-black relative overflow-hidden" data-id="element-586">
      {/* Subtle background glow */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-900/30 via-black to-black pointer-events-none" data-id="element-587"></div>

      <div className="max-w-5xl mx-auto px-4 relative z-10" data-id="element-588">
        {/* Compact Title */}
        <div className="text-center mb-6" data-id="element-589">
          <h2 className="text-xl md:text-2xl font-serif font-bold text-white mb-1" data-id="element-590">
            Tu Transformación en <span className="text-gold-500" data-id="element-591">5 Pasos</span>
          </h2>
          <p className="text-gray-500 text-xs" data-id="element-592">
            Del caos al control con el Método CIERRE 90'
          </p>
        </div>

        {/* Premium Compact Slider */}
        <div className="relative max-w-3xl mx-auto" data-id="element-593">
          {/* Main Slide - More compact with contained images */}
          <div className="relative h-[280px] md:h-[320px] rounded-xl overflow-hidden bg-gradient-to-br from-dark-900 to-black border border-gold-500/20 shadow-[0_0_40px_rgba(212,175,55,0.12)]" data-id="element-594">
            <AnimatePresence mode="wait" data-id="element-595">
              <motion.div key={currentSlide} initial={{
              opacity: 0,
              scale: 1.03
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.97
            }} transition={{
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1]
            }} className="absolute inset-0 flex items-center justify-center p-4" data-id="element-596">
                {/* Image container - centered and contained */}
                <div className="relative w-full h-full flex items-center justify-center" data-id="element-597">
                  <img src={slides[currentSlide].image} alt={slides[currentSlide].title} className="max-w-full max-h-full object-contain drop-shadow-2xl" data-id="element-598" />

                  {/* Subtle vignette effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" data-id="element-599"></div>
                </div>

                {/* Elegant floating label */}
                <motion.div initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2,
                duration: 0.4
              }} className="absolute bottom-4 left-4 right-4 flex items-center justify-between" data-id="element-600">
                  <div className="bg-black/60 backdrop-blur-md px-4 py-2 rounded-lg border border-white/10" data-id="element-601">
                    <span className="text-gold-500 text-[10px] font-bold uppercase tracking-widest block mb-0.5" data-id="element-602">
                      {slides[currentSlide].title}
                    </span>
                    <h3 className="text-sm md:text-base font-serif font-bold text-white leading-tight" data-id="element-603">
                      {slides[currentSlide].subtitle}
                    </h3>
                  </div>

                  {/* Slide counter */}
                  <div className="bg-black/60 backdrop-blur-md px-3 py-2 rounded-lg border border-white/10" data-id="element-604">
                    <span className="text-white text-xs font-mono" data-id="element-605">
                      {String(currentSlide + 1).padStart(2, '0')}/
                      {String(slides.length).padStart(2, '0')}
                    </span>
                  </div>
                </motion.div>
              </motion.div>
            </AnimatePresence>

            {/* Minimal elegant navigation */}
            <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/70 backdrop-blur-md border border-white/10 rounded-full flex items-center justify-center transition-all duration-300 group z-10" aria-label="Previous" data-id="element-606">
              <ChevronLeft className="w-4 h-4 text-white/60 group-hover:text-white transition-colors" data-id="element-607" />
            </button>

            <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/70 backdrop-blur-md border border-white/10 rounded-full flex items-center justify-center transition-all duration-300 group z-10" aria-label="Next" data-id="element-608">
              <ChevronRight className="w-4 h-4 text-white/60 group-hover:text-white transition-colors" data-id="element-609" />
            </button>
          </div>

          {/* Refined dot indicators with progress */}
          <div className="flex justify-center items-center gap-2 mt-4" data-id="element-610">
            {slides.map((_, index) => <button key={index} onClick={() => goToSlide(index)} className="relative group" aria-label={`Go to slide ${index + 1}`} data-id="element-611">
                <div className={`transition-all duration-500 rounded-full ${index === currentSlide ? 'w-7 h-1 bg-gold-500' : 'w-1 h-1 bg-gray-700 group-hover:bg-gray-600'}`} data-id="element-612">
                  {index === currentSlide && isAutoPlaying && <motion.div className="absolute inset-0 bg-gold-400 rounded-full origin-left" initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 3.5,
                ease: 'linear'
              }} data-id="element-613" />}
                </div>
              </button>)}
          </div>

          {/* Minimal thumbnail preview - only on larger screens */}
          <div className="hidden lg:flex justify-center gap-2 mt-6" data-id="element-614">
            {slides.map((slide, index) => <button key={index} onClick={() => goToSlide(index)} className={`relative w-16 h-16 rounded-lg overflow-hidden border transition-all duration-300 bg-dark-900 ${index === currentSlide ? 'border-gold-500 scale-105 shadow-[0_0_12px_rgba(212,175,55,0.3)]' : 'border-white/10 opacity-40 hover:opacity-70 hover:border-white/30'}`} data-id="element-615">
                <img src={slide.image} alt={slide.title} className="w-full h-full object-contain p-1" data-id="element-616" />
                {index === currentSlide && <div className="absolute inset-0 border-2 border-gold-500 rounded-lg" data-id="element-617"></div>}
              </button>)}
          </div>
        </div>

        {/* Subtle decorative line */}
        <div className="mt-8 max-w-xs mx-auto h-px bg-gradient-to-r from-transparent via-gold-500/20 to-transparent" data-id="element-618"></div>
      </div>
    </section>;
}